import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, ErrorMessage, Field, Loading, ModuleHeader, Tabs, Toggle, useAsync, useToast } from '../../components/ui';
import { AppearanceSettings } from './AppearanceSettings';
import { ChangelogSettings } from './ChangelogSettings';
import { StorageSettings } from './StorageSettings';
import { DsfaBadge } from '../../components/DsfaBadge';
import { api } from '../../lib/api';
import { IconBox, IconInfo, IconPalette, IconSettings } from '../../components/icons';

/** Systemweite Parameter (Kap. 9 und 17). */
export function SettingsPage() {
  const { notify } = useToast();
  const settings = useAsync(() => api.get('/api/catalogs/settings'), []);

  type TabKey = 'parameter' | 'erscheinung' | 'daten' | 'aenderungsverlauf';
  const [searchParams] = useSearchParams();
  const angefragt = searchParams.get('tab');
  const [tab, setTab] = useState<TabKey>(
    angefragt === 'erscheinung' || angefragt === 'daten' || angefragt === 'aenderungsverlauf' ? angefragt : 'parameter',
  );
  const [values, setValues] = useState<Record<string, any>>({});
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (settings.data) setValues(settings.data);
  }, [settings.data]);

  if (settings.loading) return <Loading />;

  const set = (key: string, value: any) => setValues((current) => ({ ...current, [key]: value }));

  const save = async () => {
    try {
      await api.put('/api/catalogs/settings', values);
      notify('Einstellungen gespeichert.', 'success');
      settings.reload();
    } catch (caught) {
      setError(caught);
    }
  };

  return (
    <div className="stack">
      <ModuleHeader
        icon={<IconSettings width={22} height={22} />}
        title="Systemeinstellungen"
        subtitle="Parameter für Materialanforderungen, Zeiterfassung und Datenschutz."
        pill="System"
        tone="primary"
        actions={
          tab === 'parameter' && (
            <button className="btn primary" onClick={save}>
              Speichern
            </button>
          )
        }
      />

      <ErrorMessage error={error} />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { key: 'parameter', label: 'Parameter', icon: <IconSettings width={15} height={15} /> },
          { key: 'erscheinung', label: 'Erscheinungsbild', icon: <IconPalette width={15} height={15} /> },
          { key: 'daten', label: 'Datenbank & Datensicherung', icon: <IconBox width={15} height={15} /> },
          { key: 'aenderungsverlauf', label: 'Änderungsverlauf', icon: <IconInfo width={15} height={15} /> },
        ]}
      />

      {tab === 'aenderungsverlauf' ? (
        <ChangelogSettings />
      ) : tab === 'erscheinung' ? (
        <AppearanceSettings />
      ) : tab === 'daten' ? (
        <StorageSettings />
      ) : (
        <div className="grid cols-2">
          <Card title="Materialanforderungen">
            <Field
              label="Freigabegrenze (€)"
              hint="Ab diesem Nettobetrag durchläuft eine Bestellanforderung den Freigabeworkflow."
            >
              <input
                type="number"
                min="0"
                step="10"
                value={values['procurement.approvalThreshold'] ?? 250}
                onChange={(event) => set('procurement.approvalThreshold', Number(event.target.value))}
              />
            </Field>
          </Card>

          <Card title="Zeiterfassung">
            <Field label="Standard-Stundensatz (€)" hint="Grundlage für die Kostenkalkulation je Auftrag.">
              <input
                type="number"
                min="0"
                step="1"
                value={values['time.defaultHourlyRate'] ?? 55}
                onChange={(event) => set('time.defaultHourlyRate', Number(event.target.value))}
              />
            </Field>
          </Card>

          <Card title="Anwesenheitsnachweis">
            <div className="stack">
              <Toggle
                checked={values['presence.geofenceEnabled'] ?? true}
                onChange={(value) => set('presence.geofenceEnabled', value)}
                label={
                  <>
                    Ortung (Geofence) als Nachweis zulassen <DsfaBadge id="presence.geofenceEnabled" />
                  </>
                }
              />
              <Toggle
                checked={values['presence.required'] ?? false}
                onChange={(value) => set('presence.required', value)}
                label={
                  <>
                    Anwesenheitsnachweis ist Pflicht <DsfaBadge id="presence.required" />
                  </>
                }
              />
              <div className="small subtle">
                Aus bleiben NFC und manuelle Buchung; Standortdaten werden dann weder geprüft noch gespeichert.
              </div>
            </div>
          </Card>

          <Card title="Datenschutz & Benachrichtigungen">
            <div className="stack">
              <Field
                label={
                  <>
                    Aufbewahrungsfrist (Monate) <DsfaBadge id="privacy.retentionMonths" />
                  </>
                }
                hint="Nach dieser Frist können personenbezogene Bewegungsdaten gelöscht werden (Löschkonzept, Kap. 17)."
              >
                <input
                  type="number"
                  min="1"
                  value={values['privacy.retentionMonths'] ?? 24}
                  onChange={(event) => set('privacy.retentionMonths', Number(event.target.value))}
                />
              </Field>
              <Toggle
                checked={values['notifications.emailEnabled'] ?? true}
                onChange={(value) => set('notifications.emailEnabled', value)}
                label="E-Mail-Benachrichtigungen an Melder versenden"
              />
              <Toggle
                checked={values['absences.icalSubscriptions'] ?? true}
                onChange={(value) => set('absences.icalSubscriptions', value)}
                label={
                  <>
                    Kalender-Abos für Abwesenheiten erlauben (private Kalender werden stündlich abgerufen){' '}
                    <DsfaBadge id="absences.icalSubscriptions" />
                  </>
                }
              />
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
